import { memo, useCallback, useEffect, useState } from 'react';

import filesize from 'filesize';

import { cn } from '@/shadcn/lib/utils';

import { FileIcon } from '@/components/fileIcon/fileIcon';
import { Ellipsis } from '@/components/ui/ellipsis/ellipsis';
import LoadingIndicator from '@/components/ui/LoadingIndicator';
import { useSelectableItemsContext } from '@/components/ui/widget/contexts/selectableItems';
import type { FileView } from '@/components/ui/widget/views/file/file.view.type';
import type { WidgetData } from '@/components/ui/widget/widget.type';
import { formatDateWithoutSeconds } from '@/utils';
import { downloadFile } from '@/utils/downloadFile';
import { getByPath } from '@/utils/widgets';

const DEFAULT_FILE_PATHS = {
  USER_NAME: 'upload.user',
  DATE: 'upload.date'
};

type Props = {
  view: FileView;
  data: WidgetData;
  setSelectableItemData?: (data: unknown) => void;
};

export const FileViewImpl = memo(
  ({ view, data, setSelectableItemData }: Props) => {
    const selectableItemsContext = useSelectableItemsContext();

    const [loading, setLoading] = useState(false);
    const [downloadResponse, setDownloadResponse] = useState(null);

    const {
      fileDataPath = '',
      uploadUserNamePath = DEFAULT_FILE_PATHS.USER_NAME,
      uploadDatePath = DEFAULT_FILE_PATHS.DATE
    } = view;

    const fileData = getByPath(data, fileDataPath);
    const uploadUserName = getByPath(data, uploadUserNamePath);
    const uploadDate = getByPath(data, uploadDatePath);

    useEffect(() => {
      if (setSelectableItemData) setSelectableItemData(fileData);
    }, [fileData]);

    const downloadFileFn = useCallback(
      () => downloadFile(fileData, setLoading, loading, setDownloadResponse),
      [fileData]
    );

    if (!fileData || typeof fileData !== 'object') return null;

    return (
      <div className="relative">
        <LoadingIndicator
          showSuccess={
            downloadResponse !== null || !!selectableItemsContext?.isCompleted
          }
          spinning={loading}
        >
          <div
            className={cn(
              'flex cursor-pointer flex-row flex-nowrap items-center justify-between gap-2.5 overflow-hidden whitespace-nowrap rounded-md border border-border bg-accent p-2.5 text-accent-foreground hover:text-primary',
              loading && 'opacity-50'
            )}
            data-cy="file-link"
            onClick={downloadFileFn}
          >
            <div className="flex grow flex-row flex-nowrap items-center gap-2.5 overflow-hidden">
              <FileIcon className="w-5" name={fileData.name} />
              <div className="">
                <Ellipsis className="overflow-hidden text-xs">
                  {fileData.name}
                </Ellipsis>
                <div className="text-[10px] text-muted-foreground/80">
                  {filesize(fileData.size)}
                </div>
              </div>
            </div>
            <div className="flex flex-col flex-nowrap text-[10px]">
              {uploadUserName && <div>{uploadUserName}</div>}
              {uploadDate && (
                <div className="text-muted-foreground/80">
                  {formatDateWithoutSeconds(uploadDate)}
                </div>
              )}
            </div>
          </div>
        </LoadingIndicator>
      </div>
    );
  }
);

// sort defaults to text on datePath (text is ok assuming iso dates)
const getSortConfig = view => ({
  type: 'text',
  path: view.datePath || DEFAULT_FILE_PATHS.DATE
});

export const fileViewProvider = {
  component: FileViewImpl,
  getSortConfig
};
